import { useState, useEffect } from 'react';
import useEnv from '@shared/useEnv';
import classnames from 'classnames';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { parse, format } from 'date-fns';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ListingSearchCriteria } from '@components/layouts/Truva/ListingSearchDeprecated/utils';
import { dateFormat } from '@shared/TranslationHelpers/formatDate';
import { RadioInput } from '@components/shared';
import { Possession } from '@root/types/Listing';
import { ListingSearchFilterTypes, Locale } from '@root/types';
import actions from '@store/actions/listingSearchPage';
import s from '../Filters.module.less';
import useHighlightedClick from '../useHighlightedClick';

export const possessionOptions: Array<{
  label: string;
  translationKey: string;
  value: Possession;
}> = [
  {
    label: 'now',
    value: 'now',
    translationKey: 'filters:possession:now',
  },
  {
    label: 'Within 3 months',
    value: 'threeMonths',
    translationKey: 'filters:possession:threeMonths',
  },
  {
    label: 'Within 6 months',
    value: 'sixMonths',
    translationKey: 'filters:possession:sixMonths',
  },
  {
    label: 'Within 12 months',
    value: 'oneYear',
    translationKey: 'filters:possession:oneYear',
  },
];

type Props = {
  criteria: ListingSearchCriteria;
  onChange: (criteria: ListingSearchCriteria) => void;
  type: ListingSearchFilterTypes;
};

const PossessionFilterList = ({ criteria, onChange, type }: Props) => {
  const { t } = useTranslation('filters');
  const { locale } = useEnv();
  const dispatch = useDispatch();
  const [selectedPossession, setSelectedPossession] = useState(criteria.possession());
  const [currentCustomDate, setCurrentCustomDate] = useState(new Date());
  const { setHighlighted, isHighlighted } = useHighlightedClick();

  const isPossessionCustomDate = possession => {
    const preDefinedRanges = possessionOptions.map(option => option.value);
    return !preDefinedRanges.includes(possession);
  };

  useEffect(() => {
    const possessionFromCriteria = criteria.possession();
    if (possessionFromCriteria && isPossessionCustomDate(possessionFromCriteria)) {
      const customDateObject = new Date(parse(possessionFromCriteria, 'dd/MM/yyyy', new Date()));
      setCurrentCustomDate(customDateObject);
      setSelectedPossession(format(customDateObject, 'dd/MM/yyyy'));
    } else {
      setSelectedPossession(criteria.possession());
    }
  }, [criteria]);

  const formattedCurrentCustomDate = () => {
    if (currentCustomDate === null) {
      setCurrentCustomDate(new Date());
    }
    return format(currentCustomDate, 'dd/MM/yyyy');
  };

  const getHighlightValueFromPossession = possession =>
    isPossessionCustomDate(possession) ? 'customDate' : possession;

  const changePossession = (possession: Possession) => () => {
    setHighlighted(getHighlightValueFromPossession(possession));
    if (selectedPossession === possession) {
      setSelectedPossession(null);
      criteria.remove('possession');
    } else {
      setSelectedPossession(possession);
      if (possession === 'customDate') {
        criteria.add('possession', formattedCurrentCustomDate());
      } else {
        criteria.add('possession', possession);
      }
    }
    dispatch(actions.possessionFilterChanged(criteria.possession()));
    onChange(criteria);
  };

  const onDatepickerChange = value => {
    if (selectedPossession && isPossessionCustomDate(selectedPossession) && value === null) {
      changePossession(selectedPossession);
    } else {
      setCurrentCustomDate(value || new Date());
    }
  };

  return (
    <ul className={s.filterList}>
      {possessionOptions.map(possessionOption => (
        <li
          className={classnames(isHighlighted(possessionOption.value) && s.highlighted)}
          key={possessionOption.value}
        >
          <RadioInput
            name={`possession-${possessionOption.value}-${type}`}
            value={possessionOption.value}
            checked={selectedPossession === possessionOption.value}
            onChange={changePossession(possessionOption.value)}
            labelText={t(possessionOption.translationKey)}
            id={`possession-${possessionOption.value}-${type}`}
            clearable
          />
        </li>
      ))}
      <li
        className={classnames(s.customListItem, isHighlighted('customDate') && s.highlighted)}
        key="customDate"
      >
        <RadioInput
          name={`possession-customDate-${type}`}
          value={formattedCurrentCustomDate()}
          onChange={currentCustomDate ? changePossession(formattedCurrentCustomDate()) : () => {}}
          checked={selectedPossession === formattedCurrentCustomDate()}
          labelText={
            <DatePicker
              className={s.datePickerInput}
              selected={currentCustomDate}
              disabledKeyboardNavigation
              onChange={onDatepickerChange}
              calendarClassName={s.calendarContainer}
              dateFormat={dateFormat(locale as Locale)}
              dayClassName={date =>
                format(date, 'MM/dd/yyyy') === format(currentCustomDate, 'MM/dd/yyyy')
                  ? s.selectedDatePickerDay
                  : s.datePickerDay
              }
            />
          }
          id={`possession-customDate-${type}`}
          clearable
        />
      </li>
    </ul>
  );
};
export default PossessionFilterList;
